import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import {
  SANKEY_FONT,
  type SankeyLayout,
} from "@/lib/tool-kit/diagrams-more/sankey";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A Sankey diagram from `layoutSankey`. Ribbons are cubic curves stroked at their exact
 * width and drawn first, so the node bars and their labels sit on top. Labels carry a
 * white halo (a white stroke painted under the letters) so a ribbon passing behind a
 * label never makes it hard to read.
 */

const LINK_OPACITY = 0.45;

export type SankeyLinkColor = "source" | "target" | "grey";

export function SankeyDiagram({
  id,
  layout,
  nodeColors,
  linkColor,
  title,
  label,
  description,
}: {
  id: string;
  layout: SankeyLayout;
  /** One colour per node, by node id. */
  nodeColors: readonly string[];
  linkColor: SankeyLinkColor;
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const lineHeight = SANKEY_FONT * LINE_HEIGHT;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        {layout.links.map((entry) => {
          const middle = (entry.x0 + entry.x1) / 2;
          let stroke: string = CHART_INK.axis;
          if (linkColor === "source") {
            stroke = nodeColors[entry.link.source];
          } else if (linkColor === "target") {
            stroke = nodeColors[entry.link.target];
          }
          return (
            <path
              d={`M${px(entry.x0)} ${px(entry.y0)}C${px(middle)} ${px(entry.y0)} ${px(middle)} ${px(entry.y1)} ${px(entry.x1)} ${px(entry.y1)}`}
              fill="none"
              key={`link-${entry.link.source}-${entry.link.target}`}
              stroke={stroke}
              strokeOpacity={LINK_OPACITY}
              strokeWidth={px(entry.width)}
            />
          );
        })}
        {layout.nodes.map((entry) => (
          <rect
            fill={nodeColors[entry.node.id]}
            height={px(entry.rect.height)}
            key={`node-${entry.node.id}`}
            stroke={CHART_INK.text}
            strokeWidth={0.75}
            width={px(entry.rect.width)}
            x={px(entry.rect.x)}
            y={px(entry.rect.y)}
          />
        ))}
        {layout.nodes.map((entry) => {
          const { label: text } = entry;
          const x =
            text.anchor === "end" ? text.rect.x + text.rect.width : text.rect.x;
          return (
            <g
              key={`label-${entry.node.id}`}
              paintOrder="stroke"
              stroke={CHART_INK.background}
              strokeLinejoin="round"
              strokeWidth={3}
            >
              <TextLines
                anchor={text.anchor}
                fill={CHART_INK.text}
                fontSize={SANKEY_FONT}
                lineHeight={lineHeight}
                lines={text.lines}
                top={text.rect.y}
                x={x}
              />
            </g>
          );
        })}
      </g>
    </ChartSvg>
  );
}
